export type IntegrationMode = 'mock' | 'hybrid' | 'live' | 'disabled';
export type FreshnessState = 'fresh' | 'aging' | 'stale' | 'unknown';
export type RoleId =
  | 'national-watch-officer' | 'incident-commander' | 'drone-operator'
  | 'fire-analyst' | 'field-responder' | 'public-health-officer'
  | 'prevention-planner' | 'system-admin' | 'public-viewer';

export interface PointGeometry { type: 'Point'; coordinates: [number, number] }
export interface Entity { id: string; [key: string]: unknown }
export interface DatasetEnvelope<T> {
  schemaVersion: string;
  datasetMode: string;
  generatedAt: string;
  timezone: string;
  warning: string;
  [collection: string]: T | string | unknown;
}
export interface DomainEvent<T = unknown> {
  id: string;
  type: string;
  emittedAt: string;
  scenarioTime: string;
  entityId?: string;
  payload: T;
}
export interface ApiError {
  error: { code: string; message: string; details?: unknown; requestId: string; retryable: boolean };
}
export interface ScenarioRuntime {
  scenarioId: string;
  scenarioTime: string;
  playing: boolean;
  speed: number;
  processedEventKeys: string[];
}
export interface AuditEvent extends Entity {
  action: string;
  actorId: string;
  actorRole: RoleId | string;
  entityId?: string;
  at: string;
  rationale?: string;
  before?: unknown;
  after?: unknown;
}

export const API_BASE = '/api/mock/v1';
export const PUBLIC_API_BASE = '/api/public/v1';
export const screenHelpTopics: Record<string, string> = {
  overview: 'overview-readiness', map: 'national-map', alerts: 'alert-fusion', cameras: 'camera-wall',
  drones: 'drone-fleet', incidents: 'incident-record', simulation: 'fire-spread', prevention: 'risk-forecast',
  peat: 'peat-twin', resources: 'resource-registry', smoke: 'smoke-simulation', mopup: 'mopup-thermal',
  replay: 'after-action-replay', data: 'sensor-health', help: 'help-center', admin: 'scenario-builder'
};

export function freshnessOf(value?: string, now = Date.now()): FreshnessState {
  if (!value || Number.isNaN(Date.parse(value))) return 'unknown';
  const ageMinutes = (now - Date.parse(value)) / 60_000;
  if (ageMinutes <= 15) return 'fresh';
  if (ageMinutes <= 60) return 'aging';
  return 'stale';
}

export function readinessScore(input: {
  criticalAlerts: number; activeIncidents: number; availableResources: number; staleSources: number; fwi: number;
}) {
  const risk = Math.min(100, input.fwi + input.criticalAlerts * 12 + input.activeIncidents * 10 + input.staleSources * 7);
  const capacity = Math.min(25, input.availableResources * 5);
  const score = Math.max(0, Math.round(100 - risk + capacity));
  return { score, state: score < 35 ? 'critical' : score < 60 ? 'elevated' : score < 80 ? 'guarded' : 'ready' };
}
